import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "444",
  "slug": "hurricane-typhoon-cyclone-difference",
  "title": "Hurricane, Typhoon, or Cyclone: What's the Difference?",
  "excerpt": "Hurricanes, typhoons, and cyclones sound like three different storms. They are actually the same thing with three different names. Here is what really sets them apart.",
  "date": "2026-06-15",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxodXJyaWNhbmUlMjBmcm9tJTIwc3BhY2V8ZW58MXwwfHx8MTc4MTQyNTA3N3ww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "They are all the same kind of storm — a tropical cyclone. The name just depends on where it forms: hurricane in the Atlantic and northeast Pacific, typhoon in the northwest Pacific, and cyclone in the South Pacific and Indian Ocean.",
  "content": [
    {
      "type": "paragraph",
      "text": "It is one of geography's great trick questions: what is the difference between a hurricane, a typhoon, and a cyclone? The honest answer is that there isn't a meaningful physical difference at all. They are three regional names for the exact same weather phenomenon — a tropical cyclone. Which word you use comes down to where on the map the storm spins up."
    },
    {
      "type": "heading",
      "text": "One Storm, Three Names"
    },
    {
      "type": "paragraph",
      "text": "A tropical cyclone is a large, rotating storm system that forms over warm ocean water, with a low-pressure center, organized thunderstorms, and powerful winds spiraling inward. Meteorologists in different ocean basins simply call it different things:"
    },
    {
      "type": "list",
      "items": [
        "Hurricane — in the North Atlantic and the northeastern Pacific (the storms that hit the U.S., Mexico, and the Caribbean)",
        "Typhoon — in the northwestern Pacific (the storms that affect Japan, the Philippines, China, and Southeast Asia)",
        "Cyclone — in the South Pacific and the Indian Ocean (the storms that strike India, Bangladesh, East Africa, and Australia)"
      ]
    },
    {
      "type": "paragraph",
      "text": "Same storm, same physics. A hurricane that crossed from the Atlantic into the western Pacific would, by convention, be relabeled a typhoon."
    },
    {
      "type": "heading",
      "text": "What Makes a Tropical Cyclone"
    },
    {
      "type": "paragraph",
      "text": "These storms need a specific recipe: ocean water that is warm to a good depth (generally around 26.5°C / 80°F or warmer), plenty of moisture, and relatively calm upper-level winds so the storm can build vertically instead of being sheared apart. Warm water is the fuel — it feeds rising moist air, which releases heat as it condenses, which drives the whole engine harder."
    },
    {
      "type": "paragraph",
      "text": "There is also a wind threshold. A tropical system is upgraded to hurricane, typhoon, or cyclone status once its sustained winds reach roughly 119 km/h (74 mph). Below that, it is a weaker tropical storm or depression."
    },
    {
      "type": "heading",
      "text": "How They're Categorized"
    },
    {
      "type": "paragraph",
      "text": "In the Atlantic and eastern Pacific, hurricanes are rated on the Saffir–Simpson scale from Category 1 to Category 5, based on sustained wind speed, with Category 5 the most violent. Other basins use their own classification scales, but the idea is the same: a quick shorthand for how dangerous a storm's winds are."
    },
    {
      "type": "heading",
      "text": "Why Wind Category Isn't the Whole Story"
    },
    {
      "type": "paragraph",
      "text": "It is tempting to judge a storm by its category number, but the wind rating doesn't capture what actually causes most of the damage and loss of life. Two other hazards often matter more:"
    },
    {
      "type": "list",
      "items": [
        "Storm surge — the wall of seawater a cyclone pushes ashore, which can flood low-lying coasts far beyond the reach of the wind",
        "Rainfall flooding — slow-moving storms can dump enormous amounts of rain inland, causing rivers to burst long after the winds have eased"
      ]
    },
    {
      "type": "paragraph",
      "text": "A relatively modest Category 1 storm that stalls over land can be deadlier than a fierce Category 4 that races through quickly. That is why forecasters increasingly stress water, not just wind."
    },
    {
      "type": "heading",
      "text": "Why They Spin in Opposite Directions"
    },
    {
      "type": "paragraph",
      "text": "Here is a genuine difference, and it comes down to which hemisphere you are in. Because of the Coriolis effect — the deflection caused by Earth's rotation — tropical cyclones spin counterclockwise in the Northern Hemisphere and clockwise in the Southern Hemisphere. It is also why these storms can't form right at the equator, where the Coriolis effect is too weak to start the rotation."
    },
    {
      "type": "heading",
      "text": "Spotting One From Space"
    },
    {
      "type": "paragraph",
      "text": "From orbit, a mature tropical cyclone is unmistakable: a vast pinwheel of cloud, hundreds of kilometers across, often with a clear circular \"eye\" at the center where air is sinking and skies are calm. Weather satellites track these storms continuously, which is a huge part of why modern forecasts can warn coastal communities days in advance."
    },
    {
      "type": "heading",
      "text": "How Forecasts Got So Much Better"
    },
    {
      "type": "paragraph",
      "text": "It is easy to forget how recently tropical cyclones could strike with little warning. Before weather satellites, a storm far out at sea was largely invisible until ships or coastal stations stumbled into it. Today a combination of satellites, hurricane-hunter aircraft, ocean buoys, and computer models lets forecasters estimate a storm's track and intensity days ahead, and the average error in those track forecasts has shrunk dramatically over recent decades. That lead time is what gives coastal communities the chance to board up, evacuate, and move boats and livestock to safety — turning what was once a sudden catastrophe into something people can prepare for."
    }
  ]
};
